import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  ShieldCheck, AlertTriangle, CheckCircle2, Eye, EyeOff, Loader2, Search,
  Lock, ArrowLeft, ArrowRight, Camera, Sparkles, FileText, Mail, Link2,
  User, Receipt, CreditCard, Check, Copy, Trash2, Paperclip, Download,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------- Formatting helpers ----------
const formatSSN = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 9);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
};
const formatPhone = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};
const formatDigits = (v, max) => (v || "").replace(/\D/g, "").slice(0, max);

const STEPS = [
  { id: 1, title: "Personal Info", icon: User, color: "#A61B1B" },
  { id: 2, title: "Tax Setup",     icon: Receipt, color: "#92400E" },
  { id: 3, title: "Direct Deposit", icon: CreditCard, color: "#1A5E1F" },
  { id: 4, title: "Review & Save", icon: Check, color: "#1F2937" },
];

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function CredentialsVault({ masterCode }) {
  const [view, setView] = useState("list");   // list | wizard | invites
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const headers = { "X-Master-Code": masterCode };

  const loadList = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/payroll/vault/list`, { headers });
      setList(r.data.employees || []);
    } catch (err) {
      toast.error("Failed to load: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (masterCode) loadList(); }, [masterCode]);

  const openWizard = (code) => {
    setSelected(code);
    setView("wizard");
  };

  const closeWizard = () => {
    setSelected(null);
    setView("list");
    loadList();
  };

  const filtered = useMemo(() => {
    let r = list;
    if (filter === "missing") r = r.filter(e => !e.on_file || (e.missing_fields || []).length > 0);
    if (filter === "on_file") r = r.filter(e => e.on_file && (e.missing_fields || []).length === 0);
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter(e => e.name.toLowerCase().includes(q) || e.employee_code.includes(q));
    }
    return r;
  }, [list, filter, search]);

  if (view === "wizard" && selected) {
    return <Wizard employeeCode={selected} masterCode={masterCode} onClose={closeWizard} />;
  }

  if (view === "invites") {
    return <InvitesPanel masterCode={masterCode} onBack={() => setView("list")} />;
  }

  // ---- ROSTER VIEW ----
  return (
    <div className="space-y-3">
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3">
        <ShieldCheck className="w-6 h-6 text-emerald-700 mt-0.5 shrink-0" />
        <div className="flex-1">
          <h3 className="font-bold text-emerald-900">Employee Credentials Vault</h3>
          <p className="text-emerald-800 text-sm mt-0.5">
            Add a new team member, snap a pic of their ID/W-4/voided check, and we&apos;ll auto-fill the form.
            Everything sensitive is encrypted with bank-grade AES. Only you (master) can ever see this data.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setView("invites")} className="shrink-0" data-testid="open-invites">
          <Mail className="w-4 h-4 mr-1" /> Invites
        </Button>
        <BulkExportButton masterCode={masterCode} />
      </div>

      <div className="bg-white rounded-lg p-3 shadow-sm flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-stone-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or employee code…" className="border-0 shadow-none focus-visible:ring-0" data-testid="vault-search" />
        </div>
        <div className="flex gap-1">
          {[
            { id: "all", label: "All", count: list.length },
            { id: "missing", label: "Needs Info", count: list.filter(e => !e.on_file || (e.missing_fields || []).length > 0).length },
            { id: "on_file", label: "Complete", count: list.filter(e => e.on_file && (e.missing_fields || []).length === 0).length },
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 rounded text-xs font-semibold flex items-center gap-1 ${filter === f.id ? "bg-[#A61B1B] text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"}`}
              data-testid={`vault-filter-${f.id}`}
            >
              {f.label} <span className="opacity-70">({f.count})</span>
            </button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={loadList}>Refresh</Button>
      </div>

      {loading ? (
        <div className="bg-white rounded-lg p-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /> Loading roster…</div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-100 text-xs">
              <tr>
                <th className="text-left px-3 py-2">Employee</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">Dept</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(e => {
                const complete = e.on_file && (e.missing_fields || []).length === 0;
                const partial = e.on_file && (e.missing_fields || []).length > 0;
                return (
                  <tr key={e.employee_code} className="border-b border-stone-100 hover:bg-stone-50">
                    <td className="px-3 py-2.5 font-medium">{e.name} <span className="text-xs text-stone-400">#{e.employee_code}</span></td>
                    <td className="px-3 py-2.5">
                      <span className={`text-xs px-2 py-0.5 rounded ${e.pay_category === "1099" ? "bg-purple-100 text-purple-900" : "bg-blue-100 text-blue-900"}`}>
                        {e.pay_category === "1099" ? "1099" : "W-2"}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-stone-600">{e.department}</td>
                    <td className="px-3 py-2.5">
                      {complete ? (
                        <span className="text-xs text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded font-bold inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Complete</span>
                      ) : partial ? (
                        <span className="text-xs text-amber-900 bg-amber-100 px-2 py-0.5 rounded">Missing: {e.missing_fields.join(", ")}</span>
                      ) : (
                        <span className="text-xs text-rose-900 bg-rose-100 px-2 py-0.5 rounded font-bold">Empty — Add Info</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <Button size="sm" className="bg-[#A61B1B] hover:bg-[#841515]" onClick={() => openWizard(e.employee_code)} data-testid={`vault-open-${e.employee_code}`}>
                        {e.on_file ? "Edit" : "Set Up"}
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="text-center text-stone-500 py-8 text-sm">No employees match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// WIZARD — 4-step Paychex-style flow
// ============================================================================
function Wizard({ employeeCode, masterCode, onClose }) {
  const [step, setStep] = useState(1);
  const [record, setRecord] = useState(null);
  const [reveal, setReveal] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);

  const headers = { "X-Master-Code": masterCode };

  const loadRecord = async (doReveal = false) => {
    try {
      const r = await axios.get(
        `${API}/payroll/vault/${employeeCode}${doReveal ? "?reveal=true" : ""}`,
        { headers }
      );
      setRecord(r.data);
      setReveal(doReveal);
      setForm({
        legal_first_name: r.data.legal_first_name || "",
        legal_middle_name: r.data.legal_middle_name || "",
        legal_last_name: r.data.legal_last_name || "",
        preferred_name: r.data.preferred_name || "",
        ssn: doReveal ? (r.data.ssn || "") : "",
        date_of_birth: doReveal ? (r.data.date_of_birth || "") : "",
        email: r.data.email || "",
        phone: r.data.phone || "",
        address_line1: r.data.address_line1 || "",
        address_line2: r.data.address_line2 || "",
        city: r.data.city || "",
        state: r.data.state || "IL",
        zip_code: r.data.zip_code || "",
        federal_filing_status: r.data.federal_filing_status || "Single",
        federal_allowances: r.data.federal_allowances ?? 0,
        federal_extra_withholding: r.data.federal_extra_withholding ?? 0,
        il_allowances: r.data.il_allowances ?? 0,
        il_extra_withholding: r.data.il_extra_withholding ?? 0,
        bank_name: r.data.bank_name || "",
        bank_routing: doReveal ? (r.data.bank_routing || "") : "",
        bank_account: doReveal ? (r.data.bank_account || "") : "",
        bank_account_type: r.data.bank_account_type || "checking",
        emergency_contact_name: r.data.emergency_contact_name || "",
        emergency_contact_phone: r.data.emergency_contact_phone || "",
        emergency_contact_relation: r.data.emergency_contact_relation || "",
        hire_date: r.data.hire_date || "",
        i9_verified: !!r.data.i9_verified,
        w4_signed_date: r.data.w4_signed_date || "",
        notes: r.data.notes || "",
      });
    } catch (err) {
      toast.error("Load failed: " + (err.response?.data?.detail || err.message));
    }
  };

  useEffect(() => { loadRecord(false); }, [employeeCode]);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const mergeExtracted = (data) => {
    const merged = { ...form };
    Object.entries(data).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") {
        if (k === "ssn") merged[k] = formatSSN(v);
        else merged[k] = v;
      }
    });
    setForm(merged);
    toast.success("✨ Fields filled from photo — please verify before saving");
  };

  const extractDocument = async (file, doc_type) => {
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", doc_type);
      const r = await axios.post(`${API}/payroll/vault/extract-document`, fd, { headers });
      mergeExtracted(r.data.data || {});
      // Also save the attachment so it's stored
      const fd2 = new FormData();
      fd2.append("file", file);
      fd2.append("doc_type", doc_type);
      fd2.append("label", doc_type.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()));
      fd2.append("employee_code", employeeCode);
      await axios.post(`${API}/payroll/vault/attachments/upload`, fd2, { headers });
    } catch (err) {
      toast.error("AI extract failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setExtracting(false);
    }
  };

  const save = async (finalize = false) => {
    setSaving(true);
    try {
      const payload = { employee_code: employeeCode, ...form };
      ["ssn", "date_of_birth", "bank_routing", "bank_account"].forEach(f => {
        if (!payload[f] || String(payload[f]).trim() === "") delete payload[f];
      });
      await axios.post(`${API}/payroll/vault/upsert`, payload, { headers });
      toast.success(finalize ? "🎉 Saved & encrypted — all set!" : "Saved");
      if (finalize) {
        onClose();
      } else {
        await loadRecord(false);
      }
    } catch (err) {
      toast.error("Save failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (!record || !form) {
    return <div className="bg-white rounded-lg p-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /> Loading…</div>;
  }

  const is1099 = record.pay_category === "1099";
  // 1099 contractors skip Tax Setup (Step 2) & Direct Deposit form is simpler
  const totalSteps = is1099 ? 3 : 4;
  const stepDef = is1099
    ? [STEPS[0], STEPS[2], STEPS[3]]   // Personal, Direct Deposit, Review
    : STEPS;
  const currentStep = stepDef[step - 1];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-4 flex flex-wrap items-center justify-between gap-3">
        <button onClick={onClose} className="flex items-center gap-1 text-sm text-stone-700 hover:text-[#A61B1B]" data-testid="wizard-exit">
          <ArrowLeft className="w-4 h-4" /> Save & Exit
        </button>
        <div className="text-center flex-1">
          <h2 className="font-bold text-lg">{record.employee_name} <span className="text-xs text-stone-400">#{employeeCode}</span></h2>
          <p className="text-xs text-stone-500">{record.department} · {is1099 ? "1099 Contractor" : "W-2 Employee"}</p>
        </div>
        {reveal ? (
          <Button variant="outline" size="sm" onClick={() => loadRecord(false)} className="text-amber-700 border-amber-300" data-testid="wizard-hide">
            <EyeOff className="w-4 h-4 mr-1" /> Hide PII
          </Button>
        ) : (
          <Button variant="outline" size="sm" onClick={() => loadRecord(true)} data-testid="wizard-reveal">
            <Eye className="w-4 h-4 mr-1" /> Reveal Saved PII
          </Button>
        )}
      </div>

      {/* Progress bar */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex items-center justify-between">
          {stepDef.map((s, i) => {
            const Icon = s.icon;
            const active = step === (i + 1);
            const done = step > (i + 1);
            return (
              <React.Fragment key={s.id}>
                <div className="flex flex-col items-center text-center min-w-[80px]">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center mb-1.5 transition ${
                      done ? "bg-emerald-100 text-emerald-700"
                        : active ? "text-white shadow-lg"
                        : "bg-stone-100 text-stone-400"
                    }`}
                    style={active ? { backgroundColor: s.color } : {}}
                  >
                    {done ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <span className={`text-xs font-semibold ${active ? "text-stone-900" : done ? "text-emerald-700" : "text-stone-400"}`}>{s.title}</span>
                </div>
                {i < stepDef.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-1 sm:mx-3 transition ${step > (i + 1) ? "bg-emerald-300" : "bg-stone-200"}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Reveal warning */}
      {reveal && (
        <div className="bg-amber-50 border border-amber-300 rounded p-3 text-sm flex gap-2 items-start">
          <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
          <span className="text-amber-900"><strong>Reveal mode active.</strong> This view is audit-logged. Don&apos;t screen-share.</span>
        </div>
      )}

      {/* Step content */}
      <div className="bg-white rounded-lg shadow-sm p-5 sm:p-6">
        {currentStep.id === 1 && (
          <StepPersonal
            form={form}
            update={update}
            record={record}
            onExtract={extractDocument}
            extracting={extracting}
            reveal={reveal}
          />
        )}
        {currentStep.id === 2 && !is1099 && (
          <StepTax form={form} update={update} onExtract={extractDocument} extracting={extracting} />
        )}
        {currentStep.id === 3 && (
          <StepDeposit
            form={form}
            update={update}
            record={record}
            onExtract={extractDocument}
            extracting={extracting}
            reveal={reveal}
            is1099={is1099}
          />
        )}
        {currentStep.id === 4 && (
          <StepReview form={form} record={record} is1099={is1099} />
        )}
      </div>

      {/* Navigation */}
      <div className="bg-white rounded-lg shadow-sm p-4 flex flex-wrap items-center justify-between gap-3 sticky bottom-0 z-10">
        <Button
          variant="outline"
          onClick={() => setStep(Math.max(1, step - 1))}
          disabled={step === 1}
          className="h-12 px-5"
          data-testid="wizard-back"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <p className="text-xs text-stone-500 flex items-center gap-1 hidden sm:flex">
          <Lock className="w-3.5 h-3.5" /> Encrypted with AES-128. Step {step} of {totalSteps}.
        </p>
        {step < totalSteps ? (
          <Button
            onClick={async () => { await save(false); setStep(step + 1); }}
            disabled={saving}
            className="h-12 px-6 bg-[#A61B1B] hover:bg-[#841515]"
            data-testid="wizard-next"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save & Continue <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button
            onClick={() => save(true)}
            disabled={saving}
            className="h-12 px-8 bg-emerald-700 hover:bg-emerald-800"
            data-testid="wizard-finish"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            Finish & Save
          </Button>
        )}
      </div>

      {/* Attachments */}
      <AttachmentsList employeeCode={employeeCode} masterCode={masterCode} />
    </div>
  );
}

// ============================================================================
// STEP COMPONENTS
// ============================================================================

function StepPersonal({ form, update, record, onExtract, extracting, reveal }) {
  return (
    <div className="space-y-5">
      <Intro
        title="Let's add your team member"
        sub="Snap a photo of their Driver's License or SSN card and we'll fill the form for you. Or just type it in — whichever is easier."
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <PhotoExtract
          label="📸 Scan Driver's License"
          desc="Auto-fills name, DOB, address"
          icon={Camera}
          color="bg-blue-50 border-blue-200 hover:bg-blue-100"
          onPick={(f) => onExtract(f, "drivers_license")}
          loading={extracting}
          testid="scan-license"
        />
        <PhotoExtract
          label="📸 Scan SSN Card"
          desc="Auto-fills SSN + full legal name"
          icon={Camera}
          color="bg-emerald-50 border-emerald-200 hover:bg-emerald-100"
          onPick={(f) => onExtract(f, "ssn_card")}
          loading={extracting}
          testid="scan-ssn"
        />
      </div>

      <Divider>or fill in manually</Divider>

      <Section title="Legal Name (matches SSN card)">
        <Grid3>
          <Field label="First Name *"><Input value={form.legal_first_name} onChange={e => update("legal_first_name", e.target.value)} placeholder="Maria" data-testid="vault-fname" /></Field>
          <Field label="Middle"><Input value={form.legal_middle_name} onChange={e => update("legal_middle_name", e.target.value)} placeholder="Optional" /></Field>
          <Field label="Last Name *"><Input value={form.legal_last_name} onChange={e => update("legal_last_name", e.target.value)} placeholder="Garcia" data-testid="vault-lname" /></Field>
        </Grid3>
        <Field label="Preferred Name (what we call them at work)" className="mt-3">
          <Input value={form.preferred_name} onChange={e => update("preferred_name", e.target.value)} placeholder="Mary" />
        </Field>
      </Section>

      <Section title="Identity">
        <Grid2>
          <Field label="Social Security Number *">
            <Input
              value={form.ssn}
              onChange={e => update("ssn", formatSSN(e.target.value))}
              placeholder={record.has_ssn && !reveal ? record.ssn_masked || "•••-••-••••" : "123-45-6789"}
              autoComplete="off"
              data-testid="vault-ssn"
            />
            {record.has_ssn && !reveal && <Hint>On file ✓ — leave blank to keep saved value</Hint>}
          </Field>
          <Field label="Date of Birth *">
            <Input
              type="date"
              value={form.date_of_birth}
              onChange={e => update("date_of_birth", e.target.value)}
              data-testid="vault-dob"
            />
            {record.has_dob && !reveal && !form.date_of_birth && <Hint>On file ✓ — leave blank to keep saved value</Hint>}
          </Field>
        </Grid2>
      </Section>

      <Section title="Contact Info">
        <Grid2>
          <Field label="Email"><Input type="email" value={form.email} onChange={e => update("email", e.target.value)} placeholder="maria@example.com" /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={e => update("phone", formatPhone(e.target.value))} placeholder="(847) 555-0123" /></Field>
        </Grid2>
      </Section>

      <Section title="Home Address">
        <Field label="Street Address *"><Input value={form.address_line1} onChange={e => update("address_line1", e.target.value)} placeholder="4817 W Oakton St" /></Field>
        <Field label="Apartment / Unit" className="mt-3"><Input value={form.address_line2} onChange={e => update("address_line2", e.target.value)} placeholder="Apt 2B (optional)" /></Field>
        <Grid3 className="mt-3">
          <Field label="City *"><Input value={form.city} onChange={e => update("city", e.target.value)} placeholder="Skokie" /></Field>
          <Field label="State *"><Input value={form.state} onChange={e => update("state", e.target.value.toUpperCase().slice(0, 2))} placeholder="IL" /></Field>
          <Field label="ZIP *"><Input value={form.zip_code} onChange={e => update("zip_code", formatDigits(e.target.value, 5))} placeholder="60076" /></Field>
        </Grid3>
      </Section>

      <Section title="Emergency Contact (optional)">
        <Grid3>
          <Field label="Name"><Input value={form.emergency_contact_name} onChange={e => update("emergency_contact_name", e.target.value)} /></Field>
          <Field label="Phone"><Input value={form.emergency_contact_phone} onChange={e => update("emergency_contact_phone", formatPhone(e.target.value))} placeholder="(847) 555-0123" /></Field>
          <Field label="Relation"><Input value={form.emergency_contact_relation} onChange={e => update("emergency_contact_relation", e.target.value)} placeholder="Spouse, Parent…" /></Field>
        </Grid3>
      </Section>
    </div>
  );
}

function StepTax({ form, update, onExtract, extracting }) {
  return (
    <div className="space-y-5">
      <Intro
        title="Federal & Illinois tax setup"
        sub="Snap the W-4 form they filled out, or pick the filing status below. We use this to calculate how much tax to withhold each pay run."
      />

      <PhotoExtract
        label="📸 Scan W-4 Form"
        desc="Auto-fills filing status, extra withholding & signed date"
        icon={FileText}
        color="bg-amber-50 border-amber-200 hover:bg-amber-100"
        onPick={(f) => onExtract(f, "w4")}
        loading={extracting}
        testid="scan-w4"
      />

      <Divider>or pick manually</Divider>

      <Section title="Federal Withholding (IRS Form W-4)">
        <Field label="Filing Status *">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-1">
            {["Single", "Married Filing Jointly", "Head of Household"].map(opt => (
              <button
                key={opt}
                type="button"
                onClick={() => update("federal_filing_status", opt)}
                className={`p-3 rounded-lg border-2 text-sm font-semibold transition ${
                  form.federal_filing_status === opt
                    ? "border-[#A61B1B] bg-rose-50 text-[#A61B1B]"
                    : "border-stone-200 hover:border-stone-300 text-stone-700"
                }`}
                data-testid={`filing-${opt.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {opt}
              </button>
            ))}
          </div>
        </Field>
        <Grid2 className="mt-3">
          <Field label="Federal Allowances (from W-4)">
            <Input type="number" min={0} value={form.federal_allowances} onChange={e => update("federal_allowances", parseInt(e.target.value || 0))} />
            <Hint>Usually 0–3. Higher = less tax withheld.</Hint>
          </Field>
          <Field label="Extra Federal Withholding ($)">
            <Input type="number" min={0} step="0.01" value={form.federal_extra_withholding} onChange={e => update("federal_extra_withholding", parseFloat(e.target.value || 0))} />
            <Hint>Step 4(c) on Form W-4. Leave 0 if blank.</Hint>
          </Field>
        </Grid2>
      </Section>

      <Section title="Illinois State Withholding (IL-W-4)">
        <Grid2>
          <Field label="IL Allowances">
            <Input type="number" min={0} value={form.il_allowances} onChange={e => update("il_allowances", parseInt(e.target.value || 0))} />
          </Field>
          <Field label="Extra IL Withholding ($)">
            <Input type="number" min={0} step="0.01" value={form.il_extra_withholding} onChange={e => update("il_extra_withholding", parseFloat(e.target.value || 0))} />
          </Field>
        </Grid2>
      </Section>

      <Section title="Compliance">
        <Grid2>
          <Field label="W-4 Signed Date">
            <Input type="date" value={form.w4_signed_date} onChange={e => update("w4_signed_date", e.target.value)} />
          </Field>
          <Field label="I-9 Verified">
            <label className="flex items-center gap-2 h-10 px-3 border border-stone-200 rounded">
              <input type="checkbox" checked={!!form.i9_verified} onChange={e => update("i9_verified", e.target.checked)} className="w-4 h-4" />
              <span className="text-sm">{form.i9_verified ? "Yes, I-9 is on file" : "Not yet verified"}</span>
            </label>
          </Field>
        </Grid2>
      </Section>
    </div>
  );
}

function StepDeposit({ form, update, record, onExtract, extracting, reveal, is1099 }) {
  return (
    <div className="space-y-5">
      <Intro
        title={is1099 ? "Where do we pay this contractor?" : "Direct deposit bank account"}
        sub="Snap a voided check and we'll auto-fill the routing & account number. Or grab the numbers from the bottom of a check and type them in."
      />

      <PhotoExtract
        label="📸 Scan Voided Check"
        desc="Auto-fills bank name, routing & account number"
        icon={Receipt}
        color="bg-emerald-50 border-emerald-200 hover:bg-emerald-100"
        onPick={(f) => onExtract(f, "voided_check")}
        loading={extracting}
        testid="scan-check"
      />

      <Divider>or type the numbers</Divider>

      <Section title="Bank Account">
        <Grid2>
          <Field label="Bank Name"><Input value={form.bank_name} onChange={e => update("bank_name", e.target.value)} placeholder="Chase, BMO Harris, Wells Fargo…" /></Field>
          <Field label="Account Type">
            <div className="grid grid-cols-2 gap-2 mt-1">
              {["checking", "savings"].map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => update("bank_account_type", t)}
                  className={`p-3 rounded-lg border-2 text-sm font-semibold capitalize ${
                    form.bank_account_type === t
                      ? "border-emerald-700 bg-emerald-50 text-emerald-800"
                      : "border-stone-200 hover:border-stone-300 text-stone-700"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </Field>
        </Grid2>
        <Grid2 className="mt-3">
          <Field label="Routing Number (9 digits, leftmost on check)">
            <Input
              value={form.bank_routing}
              onChange={e => update("bank_routing", formatDigits(e.target.value, 9))}
              placeholder={record.has_bank_routing && !reveal ? record.bank_routing_masked : "071000013"}
              autoComplete="off"
              data-testid="vault-routing"
            />
            {form.bank_routing && form.bank_routing.length !== 9 && form.bank_routing.length > 0 && (
              <Hint danger>Routing must be exactly 9 digits ({form.bank_routing.length}/9)</Hint>
            )}
            {record.has_bank_routing && !reveal && !form.bank_routing && <Hint>On file ✓ — leave blank to keep saved value</Hint>}
          </Field>
          <Field label="Account Number">
            <Input
              value={form.bank_account}
              onChange={e => update("bank_account", formatDigits(e.target.value, 17))}
              placeholder={record.has_bank_account && !reveal ? record.bank_account_masked : "Account #"}
              autoComplete="off"
              data-testid="vault-account"
            />
            {record.has_bank_account && !reveal && !form.bank_account && <Hint>On file ✓ — leave blank to keep saved value</Hint>}
          </Field>
        </Grid2>
      </Section>

      <Section title="Employment">
        <Grid2>
          <Field label="Hire Date"><Input type="date" value={form.hire_date} onChange={e => update("hire_date", e.target.value)} /></Field>
          <Field label="Notes">
            <textarea value={form.notes} onChange={e => update("notes", e.target.value)} rows={2} className="w-full border border-stone-200 rounded px-2 py-1.5 text-sm" placeholder="Anything to remember…" />
          </Field>
        </Grid2>
      </Section>
    </div>
  );
}

function StepReview({ form, record, is1099 }) {
  // Display SSN/account with masking unless explicitly entered
  const ssnDisplay = form.ssn || record.ssn_masked || "";
  const acctDisplay = form.bank_account || record.bank_account_masked || "";
  const routDisplay = form.bank_routing || record.bank_routing_masked || "";

  return (
    <div className="space-y-5">
      <Intro title="Almost done — please double-check" sub="Hit ‘Finish & Save’ when everything looks correct. Sensitive fields stay encrypted." />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ReviewCard title="Personal" icon={User}>
          <ReviewRow label="Legal Name" value={[form.legal_first_name, form.legal_middle_name, form.legal_last_name].filter(Boolean).join(" ")} danger={!form.legal_first_name || !form.legal_last_name} />
          <ReviewRow label="Preferred Name" value={form.preferred_name} />
          <ReviewRow label="SSN" value={ssnDisplay} danger={!ssnDisplay} />
          <ReviewRow label="Date of Birth" value={form.date_of_birth || (record.has_dob ? "on file ✓" : "")} danger={!form.date_of_birth && !record.has_dob} />
          <ReviewRow label="Email" value={form.email} />
          <ReviewRow label="Phone" value={form.phone} />
          <ReviewRow label="Address" value={[form.address_line1, form.address_line2, [form.city, form.state, form.zip_code].filter(Boolean).join(" ")].filter(Boolean).join(", ")} danger={!form.address_line1} />
        </ReviewCard>

        {!is1099 && (
          <ReviewCard title="Tax Setup (W-4)" icon={Receipt}>
            <ReviewRow label="Federal Filing Status" value={form.federal_filing_status} />
            <ReviewRow label="Federal Allowances" value={String(form.federal_allowances)} />
            <ReviewRow label="Extra Federal Withholding" value={`$${Number(form.federal_extra_withholding || 0).toFixed(2)}`} />
            <ReviewRow label="IL Allowances" value={String(form.il_allowances)} />
            <ReviewRow label="Extra IL Withholding" value={`$${Number(form.il_extra_withholding || 0).toFixed(2)}`} />
            <ReviewRow label="W-4 Signed Date" value={form.w4_signed_date} />
            <ReviewRow label="I-9 Verified" value={form.i9_verified ? "Yes ✓" : "No"} />
          </ReviewCard>
        )}

        <ReviewCard title={is1099 ? "Payment Method" : "Direct Deposit"} icon={CreditCard}>
          <ReviewRow label="Bank Name" value={form.bank_name} />
          <ReviewRow label="Account Type" value={form.bank_account_type} />
          <ReviewRow label="Routing #" value={routDisplay} danger={!routDisplay} />
          <ReviewRow label="Account #" value={acctDisplay} danger={!acctDisplay} />
        </ReviewCard>

        <ReviewCard title="Employment & Emergency" icon={ShieldCheck}>
          <ReviewRow label="Hire Date" value={form.hire_date} />
          <ReviewRow label="Emergency Contact" value={form.emergency_contact_name} />
          <ReviewRow label="Emergency Phone" value={form.emergency_contact_phone} />
          <ReviewRow label="Relation" value={form.emergency_contact_relation} />
          <ReviewRow label="Notes" value={form.notes} />
        </ReviewCard>
      </div>
    </div>
  );
}

const ReviewRow = ({ label, value, danger }) => (
  <div className="flex justify-between py-1.5 border-b border-stone-100 text-sm">
    <span className="text-stone-500">{label}</span>
    <span className={`font-mono ${danger ? "text-rose-600 italic" : "text-stone-900 font-semibold"}`}>{value || (danger ? "— missing —" : "—")}</span>
  </div>
);

// ============================================================================
// ATTACHMENTS
// ============================================================================
function AttachmentsList({ employeeCode, masterCode }) {
  const [items, setItems] = useState([]);
  const headers = { "X-Master-Code": masterCode };

  const load = async () => {
    try {
      const r = await axios.get(`${API}/payroll/vault/attachments/list/${employeeCode}`, { headers });
      setItems(r.data.items || []);
    } catch { /* ignore */ }
  };

  const view = async (id) => {
    try {
      const r = await axios.get(`${API}/payroll/vault/attachments/${id}/view`, { headers });
      const w = window.open();
      if (w) {
        w.document.write(`<img src="${r.data.data_url}" style="max-width:100%" />`);
      }
    } catch (err) {
      toast.error("View failed: " + (err.response?.data?.detail || err.message));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this attachment permanently?")) return;
    try {
      await axios.delete(`${API}/payroll/vault/attachments/${id}`, { headers });
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error("Delete failed: " + (err.response?.data?.detail || err.message));
    }
  };

  useEffect(() => { load(); }, [employeeCode]);

  if (items.length === 0) return null;

  return (
    <div className="bg-white rounded-lg shadow-sm p-4">
      <h3 className="font-bold text-sm mb-3 flex items-center gap-2"><Paperclip className="w-4 h-4" /> Attached Documents ({items.length})</h3>
      <div className="space-y-1.5">
        {items.map(it => (
          <div key={it.id} className="flex items-center justify-between p-2 bg-stone-50 rounded hover:bg-stone-100">
            <div className="text-sm">
              <span className="font-semibold">{it.label}</span>
              <span className="text-xs text-stone-500 ml-2">{(it.size_bytes / 1024).toFixed(1)} KB · {new Date(it.uploaded_at).toLocaleString()}</span>
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" onClick={() => view(it.id)}><Eye className="w-3.5 h-3.5" /></Button>
              <Button size="sm" variant="outline" onClick={() => remove(it.id)} className="text-rose-700"><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// INVITES PANEL
// ============================================================================
function InvitesPanel({ masterCode, onBack }) {
  const [invites, setInvites] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedCode, setSelectedCode] = useState("");
  const [creating, setCreating] = useState(false);
  const [lastLink, setLastLink] = useState(null);

  const headers = { "X-Master-Code": masterCode };

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/payroll/vault/invite/list/all`, { headers }),
        axios.get(`${API}/payroll/vault/list`, { headers }),
      ]);
      setInvites(a.data.items || []);
      setEmployees((b.data.employees || []).filter(e => !e.on_file || (e.missing_fields || []).length > 0));
    } catch (err) {
      toast.error("Load failed: " + (err.response?.data?.detail || err.message));
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!selectedCode) { toast.error("Pick an employee first"); return; }
    setCreating(true);
    try {
      const r = await axios.post(`${API}/payroll/vault/invite/create`, { employee_code: selectedCode, expires_in_days: 7 }, { headers });
      const url = `${window.location.origin}/onboard/${r.data.token}`;
      setLastLink({ url, name: r.data.employee_name, expires_at: r.data.expires_at });
      await load();
      toast.success("Onboarding link created");
    } catch (err) {
      toast.error("Create failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setCreating(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="space-y-3">
      <Button variant="outline" size="sm" onClick={onBack} data-testid="invites-back"><ArrowLeft className="w-4 h-4 mr-1" /> Back to roster</Button>

      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 flex items-start gap-3">
        <Mail className="w-5 h-5 text-indigo-700 mt-0.5 shrink-0" />
        <div>
          <h3 className="font-bold text-indigo-900">Send a Self-Onboarding Link</h3>
          <p className="text-sm text-indigo-800 mt-0.5">Generate a secure 7-day link. The employee opens it on their phone and fills in their own SSN, W-4, and bank info — encrypted on arrival.</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-4 flex flex-wrap items-end gap-3">
        <Field label="Pick an employee" className="flex-1 min-w-[240px]">
          <select
            value={selectedCode}
            onChange={e => setSelectedCode(e.target.value)}
            className="w-full h-10 border border-stone-200 rounded px-2 text-sm"
            data-testid="invite-emp-select"
          >
            <option value="">— select —</option>
            {employees.map(e => (
              <option key={e.employee_code} value={e.employee_code}>
                {e.name} (#{e.employee_code}) · {e.pay_category === "1099" ? "1099" : "W-2"} · {e.missing_fields?.join(", ") || "missing all"}
              </option>
            ))}
          </select>
        </Field>
        <Button onClick={create} disabled={creating || !selectedCode} className="h-10 bg-indigo-700 hover:bg-indigo-800" data-testid="invite-create">
          {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Link2 className="w-4 h-4 mr-1" />}
          Generate Link
        </Button>
      </div>

      {lastLink && (
        <div className="bg-emerald-50 border border-emerald-300 rounded-lg p-4">
          <h4 className="font-bold text-emerald-900 mb-2">✅ Link for {lastLink.name}</h4>
          <div className="bg-white p-2 rounded border border-emerald-200 flex items-center gap-2">
            <code className="flex-1 text-xs text-stone-700 break-all">{lastLink.url}</code>
            <Button size="sm" variant="outline" onClick={() => copy(lastLink.url)}><Copy className="w-3.5 h-3.5 mr-1" /> Copy</Button>
          </div>
          <p className="text-xs text-emerald-800 mt-2">Expires {new Date(lastLink.expires_at).toLocaleString()}. Text or email this to the employee.</p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm p-4">
        <h3 className="font-bold text-sm mb-3">Recent Invites</h3>
        {invites.length === 0 ? (
          <p className="text-sm text-stone-500">No invites yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-stone-100 text-xs">
              <tr><th className="text-left px-2 py-1.5">Employee</th><th className="text-left px-2 py-1.5">Created</th><th className="text-left px-2 py-1.5">Expires</th><th className="text-left px-2 py-1.5">Status</th><th></th></tr>
            </thead>
            <tbody>
              {invites.map((it, i) => (
                <tr key={i} className="border-b border-stone-100">
                  <td className="px-2 py-1.5">{it.employee_name} <span className="text-xs text-stone-400">#{it.employee_code}</span></td>
                  <td className="px-2 py-1.5 text-xs">{new Date(it.created_at).toLocaleString()}</td>
                  <td className="px-2 py-1.5 text-xs">{new Date(it.expires_at).toLocaleString()}</td>
                  <td className="px-2 py-1.5">
                    {it.status === "active" && <span className="text-xs bg-blue-100 text-blue-900 px-2 py-0.5 rounded font-bold">Active</span>}
                    {it.status === "used" && <span className="text-xs bg-emerald-100 text-emerald-900 px-2 py-0.5 rounded font-bold">Used ✓</span>}
                    {it.status === "expired" && <span className="text-xs bg-stone-200 text-stone-700 px-2 py-0.5 rounded">Expired</span>}
                  </td>
                  <td className="px-2 py-1.5">
                    {it.status === "active" && (
                      <Button size="sm" variant="outline" onClick={() => copy(`${window.location.origin}/onboard/${it.token}`)}><Copy className="w-3.5 h-3.5" /></Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// SHARED UI HELPERS
// ============================================================================
const Intro = ({ title, sub }) => (
  <div>
    <h2 className="text-xl sm:text-2xl font-bold text-stone-900">{title}</h2>
    <p className="text-sm text-stone-600 mt-1">{sub}</p>
  </div>
);

const Section = ({ title, children }) => (
  <div>
    <h3 className="text-sm font-bold text-stone-800 mb-3 uppercase tracking-wider">{title}</h3>
    {children}
  </div>
);

const Grid2 = ({ children, className = "" }) => (
  <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${className}`}>{children}</div>
);
const Grid3 = ({ children, className = "" }) => (
  <div className={`grid grid-cols-1 sm:grid-cols-3 gap-3 ${className}`}>{children}</div>
);

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <Label className="text-xs text-stone-600 mb-1 block">{label}</Label>
    {children}
  </div>
);

const Hint = ({ children, danger }) => (
  <p className={`text-xs mt-1 ${danger ? "text-rose-600 font-semibold" : "text-stone-500"}`}>{children}</p>
);

const Divider = ({ children }) => (
  <div className="flex items-center gap-3 my-2">
    <div className="flex-1 h-px bg-stone-200" />
    <span className="text-xs text-stone-400 uppercase tracking-wider">{children}</span>
    <div className="flex-1 h-px bg-stone-200" />
  </div>
);

const ReviewCard = ({ title, icon: Icon, children }) => (
  <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
    <h4 className="font-bold text-sm text-stone-800 mb-2 flex items-center gap-1.5"><Icon className="w-4 h-4 text-[#A61B1B]" /> {title}</h4>
    {children}
  </div>
);

function PhotoExtract({ label, desc, icon: Icon, color, onPick, loading, testid }) {
  const ref = useRef(null);
  return (
    <div className={`border-2 border-dashed rounded-lg p-4 text-center transition cursor-pointer ${color}`} onClick={() => ref.current?.click()} data-testid={testid}>
      <input
        ref={ref}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
          e.target.value = "";
        }}
      />
      {loading ? (
        <div className="flex items-center justify-center gap-2 py-2">
          <Loader2 className="w-5 h-5 animate-spin text-stone-600" />
          <span className="text-sm font-semibold text-stone-700">AI is reading the photo…</span>
          <Sparkles className="w-4 h-4 text-amber-500" />
        </div>
      ) : (
        <>
          <Icon className="w-8 h-8 mx-auto mb-1.5 text-stone-700" />
          <p className="font-bold text-sm text-stone-900">{label}</p>
          <p className="text-xs text-stone-600 mt-0.5">{desc}</p>
          <p className="text-[10px] text-stone-400 mt-1.5">Tap to take photo or pick from files</p>
        </>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// Bulk Export → Patriot / Gusto / OnPay CSV
// ═══════════════════════════════════════════════════════════════

function BulkExportButton({ masterCode }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const openPreview = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const res = await axios.get(`${API}/payroll/vault/export/preview`, {
        headers: { "X-Master-Code": masterCode },
      });
      setPreview(res.data);
    } catch (e) {
      toast.error("Couldn't build export preview");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const download = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/payroll/vault/export/patriot-csv`, {
        headers: { "X-Master-Code": masterCode },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      const date = new Date().toISOString().slice(0, 10);
      link.setAttribute("download", `paypunch-employees-${date}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded — upload it to Patriot's Add Employees → Bulk Import");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={openPreview}
        className="shrink-0 border-amber-500 text-amber-700 hover:bg-amber-50"
        data-testid="open-bulk-export"
      >
        <Download className="w-4 h-4 mr-1" /> Bulk Export
      </Button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                  <Download className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-bold">Bulk Export to Payroll Provider</h2>
                  <p className="text-xs text-white/80 mt-0.5">
                    Patriot · Gusto · OnPay compatible CSV
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-amber-600" />
                </div>
              ) : preview ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <StatCard label="Total W-2 employees" value={preview.total} tone="neutral" />
                    <StatCard label="Ready to export" value={preview.ready_count} tone="green" />
                    <StatCard label="Missing SSN" value={preview.missing_ssn_count} tone={preview.missing_ssn_count ? "amber" : "neutral"} />
                    <StatCard label="Missing bank info" value={preview.missing_bank_count} tone={preview.missing_bank_count ? "amber" : "neutral"} />
                  </div>

                  {preview.no_vault_count > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
                      <div className="font-bold mb-1">
                        {preview.no_vault_count} employee(s) have no vault info yet:
                      </div>
                      <div className="text-amber-800">
                        {preview.no_vault.slice(0, 8).map((e) => e.name).join(", ")}
                        {preview.no_vault.length > 8 ? ", …" : ""}
                      </div>
                      <div className="mt-2 text-[11px]">
                        They&apos;ll still be in the CSV with blank sensitive fields — fill directly in Patriot after import.
                      </div>
                    </div>
                  )}

                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-900">
                    <div className="font-bold mb-1">How to use this file:</div>
                    <ol className="list-decimal list-inside space-y-0.5 text-emerald-800">
                      <li>Download the CSV below</li>
                      <li>In Patriot: Add Employees → click <b>&quot;Import from CSV&quot;</b> or <b>&quot;Bulk Add&quot;</b></li>
                      <li>Upload this file — Patriot maps the columns automatically</li>
                      <li>Fill in any gaps flagged above in Patriot&apos;s UI</li>
                    </ol>
                  </div>

                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={() => setOpen(false)} data-testid="bulk-export-cancel">
                      Cancel
                    </Button>
                    <Button
                      onClick={download}
                      disabled={downloading}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                      data-testid="bulk-export-download"
                    >
                      {downloading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Preparing…</>
                      ) : (
                        <><Download className="w-4 h-4 mr-2" /> Download CSV</>
                      )}
                    </Button>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function StatCard({ label, value, tone = "neutral" }) {
  const toneClass = {
    green: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    neutral: "bg-stone-50 border-stone-200 text-stone-900",
  }[tone];
  return (
    <div className={`rounded-lg p-3 border ${toneClass}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70 font-bold">{label}</div>
      <div className="text-2xl font-extrabold mt-0.5">{value}</div>
    </div>
  );
}
